import { useAppKit } from '@reown/appkit/react'
import { DEFAULT_TXN_DISMISS_MS } from 'constants/misc'
import { useConnection } from 'hooks/useConnection'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'state/hooks'

import { AppState } from '../index'
import { addPopup, ApplicationModal, PopupContent, removePopup, setOpenModal } from './reducer'

export function useModalIsOpen(modal: ApplicationModal): boolean {
  const openModal = useAppSelector((state: AppState) => state.application.openModal)
  return openModal === modal
}

export function useToggleModal(modal: ApplicationModal): () => void {
  const isOpen = useModalIsOpen(modal)
  const dispatch = useAppDispatch()
  return useCallback(() => dispatch(setOpenModal(isOpen ? null : modal)), [dispatch, modal, isOpen])
}

export function useOpenModal(modal: ApplicationModal): () => void {
  const dispatch = useAppDispatch()
  return useCallback(() => dispatch(setOpenModal(modal)), [dispatch, modal])
}

export function useCloseModal(_modal: ApplicationModal): () => void {
  const dispatch = useAppDispatch()
  return useCallback(() => dispatch(setOpenModal(null)), [dispatch])
}

export function useToggleWalletModal(): () => void {
  const { open } = useAppKit()
  const { isConnected } = useConnection()
  const toggleModal = useToggleModal(ApplicationModal.WALLET)
  const openAppKitModal = useCallback(() => open({ view: 'Connect' }), [open])

  return isConnected ? toggleModal : openAppKitModal
}

export function useToggleSettingsMenu(): () => void {
  return useToggleModal(ApplicationModal.SETTINGS)
}

export function useToggleSelfClaimModal(): () => void {
  return useToggleModal(ApplicationModal.SELF_CLAIM)
}

// returns a function that allows adding a popup
export function useAddPopup(): (content: PopupContent, key?: string, removeAfterMs?: number) => void {
  const dispatch = useAppDispatch()

  return useCallback(
    (content: PopupContent, key?: string, removeAfterMs?: number) => {
      dispatch(addPopup({ content, key, removeAfterMs: removeAfterMs ?? DEFAULT_TXN_DISMISS_MS }))
    },
    [dispatch]
  )
}

// returns a function that allows removing a popup via its key
export function useRemovePopup(): (key: string) => void {
  const dispatch = useAppDispatch()
  return useCallback(
    (key: string) => {
      dispatch(removePopup({ key }))
    },
    [dispatch]
  )
}

// get the list of active popups
export function useActivePopups(): AppState['application']['popupList'] {
  const list = useAppSelector((state: AppState) => state.application.popupList)
  return useMemo(() => list.filter((item) => item.show), [list])
}

export function useWalletUIModal() {
  const { open, close } = useAppKit()
  const { isConnected } = useConnection()
  const [isAccountModalOpen, setAccountModalOpen] = useState(false)

  const openConnect = useCallback(() => open({ view: 'Connect' }), [open])
  const openAccount = useCallback(() => setAccountModalOpen(true), [])
  const closeAccount = useCallback(() => setAccountModalOpen(false), [])

  // If user disconnects via AppKit, auto-close your AccountDetails
  useEffect(() => {
    if (!isConnected) setAccountModalOpen(false)
  }, [isConnected])

  return {
    isConnected,
    // For your "Connect Wallet" button
    openConnect,
    // For your status button when connected
    openAccount,
    closeAccount,
    isAccountModalOpen,
  }
}
